/** WOODWORKING FURNITURE PLANS™ — all customer-facing content and asset mapping. */

export interface PlanImage {
  src: string
  alt: string
  width: number
  height: number
}

export interface ProjectPage extends PlanImage {
  caption: string
}

export interface TechProof extends PlanImage {
  title: string
  project: string
  description: string
}

export interface Volume {
  number: string
  title: string
  projects: string
  description: string
  /** Real cover artwork. When absent, a typographic panel is shown instead. */
  cover?: PlanImage
}

export type SnapshotIcon = 'projects' | 'volumes' | 'dimensions' | 'rights'

export interface SnapshotItem {
  label: string
  icon: SnapshotIcon
}

export interface Faq {
  question: string
  answer: string
}

export const PRODUCT = {
  name: 'WOODWORKING FURNITURE PLANS™',
  descriptor: 'Furniture Build Plans for Woodworkers & Furniture Makers',
} as const

export const COMMERCIAL_RIGHTS = {
  label: 'COMMERCIAL BUILD RIGHTS INCLUDED',
  sentence: 'Build for yourself—or sell the finished furniture you make.',
} as const

export const OFFER_ANCHOR = '#offer'
export const PREVIEW_ANCHOR = '#project-preview'

export const TICKER_ITEMS: string[] = [
  '60 FURNITURE PROJECTS',
  '6 DIGITAL VOLUMES',
  'INCHES & MILLIMETERS',
  'COMMERCIAL BUILD RIGHTS',
]

export const NAV_LINKS: Array<{ label: string; href: string }> = [
  { label: 'Preview', href: '#project-preview' },
  { label: 'Inside the Plans', href: '#technical-proof' },
  { label: 'Volumes', href: '#volumes' },
  { label: 'FAQ', href: '#faq' },
]

/** All plan pages and covers share the same portrait page ratio. */
const PAGE_SIZE = { width: 1688, height: 2184 }

export const SNAPSHOT: SnapshotItem[] = [
  { label: '60 Furniture Projects', icon: 'projects' },
  { label: '6 Organized Volumes', icon: 'volumes' },
  { label: 'Inches + Millimeters', icon: 'dimensions' },
  { label: 'Build & Sell Rights', icon: 'rights' },
]

export const PROJECT_NAME = 'Project 04 — Three-Drawer Document Cabinet'

export const PROJECT_PAGES: ProjectPage[] = [
  {
    src: 'https://cdn.magicpatterns.com/uploads/opk3g39hdL7tdPJ3JxJJQ2/01_V01_P04_PDF26_finished_cabinet.png',
    caption: 'Finished Cabinet',
    alt: 'Volume 01, page 26: Project 04 Three-Drawer Document Cabinet, finished oak cabinet with front-view dimensions in millimeters and inches',
    ...PAGE_SIZE,
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/1VsccmJiQ9XGjSKUqeLsLT/02_V01_P04_PDF27_dimensioned_plans.png',
    caption: 'Dimensions & Technical Views',
    alt: 'Volume 01, page 27: dimensioned plans of the Three-Drawer Document Cabinet with front, side, top and isometric views',
    ...PAGE_SIZE,
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/3m2ZHYhfFNCMrHcDRu26yj/03_V01_P04_PDF28_exploded_view_cut_list.png',
    caption: 'Exploded View & Cut List',
    alt: 'Volume 01, page 28: exploded view of the cabinet with labeled parts and a cut list table with quantities, materials and dimensions',
    ...PAGE_SIZE,
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/o6bo9HbR8W9BxP8N6i3pGP/04_V01_P04_PDF29_wood_connections_machining.png',
    caption: 'Wood Connections & Machining',
    alt: 'Volume 01, page 29: wood connection and machining details with screw sizes, clearance and pilot holes for each joint',
    ...PAGE_SIZE,
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/ufqshk6312hhJ2FLUQxH8R/05_V01_P04_PDF30_hardware_clearances.png',
    caption: 'Hardware & Clearances',
    alt: 'Volume 01, page 30: drawer slide details, functional clearances, anti-tip restraint and hardware summary table',
    ...PAGE_SIZE,
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/e2163DFFuxeHZb7qyeiJUW/06_V01_P04_PDF31_assembly_structure.png',
    caption: 'Assembly — Structure',
    alt: 'Volume 01, page 31: structure assembly steps — labeled parts layout, plinth frame and carcass assembly',
    ...PAGE_SIZE,
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/dgy6eMuvZxxgGFWdoVoSMW/07_V01_P04_PDF32_assembly_fittings_completion.png',
    caption: 'Assembly — Fittings & Completion',
    alt: 'Volume 01, page 32: assembly steps for the carcass, drawer boxes, slides and fronts, and completion',
    ...PAGE_SIZE,
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/reXxdjgsmFhyq9FniCyNfB/08_V01_P04_PDF33_finished_reference_build_check.png',
    caption: 'Finished Reference & Build Check',
    alt: 'Volume 01, page 33: finished cabinet reference with overall dimensions, detail views and a build checklist',
    ...PAGE_SIZE,
  },
]

export const PLAN_BENEFITS: string[] = ['Plan your materials.', 'See how the parts fit.', 'Follow the build sequence.']

export const TECH_PROOF: TechProof[] = [
  {
    src: 'https://cdn.magicpatterns.com/uploads/6saZMYB2ovqjDAF1h1fcUA/01_dimensions_V01_P01_PDF07.png',
    title: 'KNOW THE DIMENSIONS',
    project: 'Project 01 · Single-Pedestal Desk with Two Drawers · Volume 01',
    description: 'Review the dimensions and technical views before cutting.',
    alt: 'Volume 01, page 7: Project 01 Single-Pedestal Desk with Two Drawers, dimensioned front, side and top views in millimeters and inches',
    ...PAGE_SIZE,
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/u5Gxrcv84RBf5RH6Yrk272/02_exploded_view_cut_list_V02_P12_PDF11.png',
    title: 'KNOW WHAT TO CUT',
    project: 'Project 12 · Coffee Table with Lower Shelf · Volume 02',
    description: 'Identify the parts, quantities, materials, and finished cut sizes.',
    alt: 'Volume 02, page 11: Project 12 Coffee Table with Lower Shelf, exploded view with part IDs and finished cut list table',
    ...PAGE_SIZE,
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/tF5ZW36wKbYHmo9naCUiCr/03_joints_hardware_V02_P11_PDF06.png',
    title: 'SEE HOW IT FITS',
    project: 'Project 11 · Three-Bay Media Console · Volume 02',
    description: 'Inspect the illustrated joint details and hardware information.',
    alt: 'Volume 02, page 6: Project 11 Three-Bay Media Console, joint and hardware details with screw specifications and cable aperture positions',
    ...PAGE_SIZE,
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/76tZgdnTzuewFP6n59CCMv/04_build_sequence_V01_P02_PDF18.png',
    title: 'FOLLOW THE BUILD',
    project: 'Project 02 · Knock-Down L-Shaped Desk · Volume 01',
    description: 'Follow the illustrated sequence from prepared parts to assembled furniture.',
    alt: 'Volume 01, page 18: Project 02 Knock-Down L-Shaped Desk, six-step illustrated build sequence from prepared parts to finished desk',
    ...PAGE_SIZE,
  },
]

export const COVER_VOLUME_01: PlanImage = {
  src: 'https://cdn.magicpatterns.com/uploads/cFHrDLBW714oNwgdveGMsf/01_cover_volume_01.png',
  alt: 'Volume 01 cover: Woodworking Furniture Plans™ — Office & Workspace',
  ...PAGE_SIZE,
}

export const COVER_VOLUME_02: PlanImage = {
  src: 'https://cdn.magicpatterns.com/uploads/5dAPwA6LTFb93p4VyUJb41/02_cover_volume_02.png',
  alt: 'Volume 02 cover: Woodworking Furniture Plans™ — Living & Media',
  ...PAGE_SIZE,
}

/** Transparent product mockup used in the hero and the offer. */
export const HERO_MOCKUP: PlanImage = {
  src: 'https://cdn.magicpatterns.com/uploads/3TMrTisWq7V7oj1B7oHTmC/Mock_up_landing.png',
  alt: 'WOODWORKING FURNITURE PLANS™ digital collection: the complete-collection cover, a tablet showing the Project 04 dimensioned plans, a printed plan sheet, an example three-drawer cabinet built from the plans, and a "5 bonus resources included free" badge',
  width: 1254,
  height: 1254,
}

export const BONUS_LINE = 'Plus 5 bonus resources, included free.'

/**
 * To add a real cover for volumes 03–06, set `cover` on that entry —
 * the card switches from the typographic panel to the image automatically.
 */
export const VOLUMES: Volume[] = [
  {
    number: '01',
    title: 'Office & Workspace',
    projects: '10 PROJECTS',
    description: 'Desks, workstations, cabinets, and practical office storage.',
    cover: COVER_VOLUME_01,
  },
  {
    number: '02',
    title: 'Living & Media',
    projects: '10 PROJECTS',
    description: 'Media consoles, coffee tables, side tables, and display furniture.',
    cover: COVER_VOLUME_02,
  },
  {
    number: '03',
    title: 'Bedroom & Personal Space',
    projects: '10 PROJECTS',
    description: 'Furniture projects for bedrooms and personal spaces.',
  },
  {
    number: '04',
    title: 'Entryway & Organization',
    projects: '10 PROJECTS',
    description: 'Furniture for entryways, storage, and everyday organization.',
  },
  {
    number: '05',
    title: 'Kitchen, Dining & Utility',
    projects: '10 PROJECTS',
    description: 'Practical furniture for dining areas, kitchens, and utility spaces.',
  },
  {
    number: '06',
    title: 'Patio, Garden & Outdoor',
    projects: '10 PROJECTS',
    description: 'Furniture projects for patios, gardens, and outdoor spaces.',
  },
]

export const OFFER_FEATURES: string[] = [
  'Dimensions and technical views in inches and millimeters',
  'Material lists and finished cut sizes',
  'Exploded views, joinery, and hardware details',
  'Illustrated assembly guidance',
  'Commercial build rights included',
  '5 bonus resources included free',
]

export const FAQS: Faq[] = [
  {
    question: 'What am I buying?',
    answer:
      'A digital collection of 60 furniture projects organized into six PDF volumes, with dimensions, materials, cut lists, technical illustrations, and assembly guidance.',
  },
  {
    question: 'Who is this collection for?',
    answer:
      'Woodworkers, furniture makers, and DIY builders with basic woodworking skills who want plans for furniture they can build for themselves or sell.',
  },
  {
    question: 'Is this a beginner woodworking course?',
    answer:
      'This collection focuses on furniture projects and build information. Familiarity with woodworking tools and basic assembly techniques will help you use the plans.',
  },
  {
    question: 'Are dimensions included in inches and millimeters?',
    answer: 'Yes. The project pages include dimensions in both inches and millimeters.',
  },
  {
    question: 'Are these physical books or furniture?',
    answer:
      'You are purchasing digital PDF files. Physical books, furniture, tools, and materials are not included.',
  },
  {
    question: 'Can I view the files on a tablet or computer?',
    answer:
      'Yes. You can view the PDFs on a tablet or computer, or print the pages you want to use in your workshop.',
  },
  {
    question: 'Can I preview a complete project?',
    answer:
      'Yes. The preview section shows all eight pages of the Three-Drawer Document Cabinet project from Volume 01.',
  },
  {
    question: 'Can I sell the furniture I build?',
    answer:
      'Yes. You may build and sell the physical furniture made from these designs. The digital plans and illustrations may not be resold, shared, or redistributed.',
  },
]

export const SUPPORT_EMAIL = 'atencion.creativodigital@gmail.com'
